import type { BulkOrderDateContext } from "../../redistribution/models/BulkOrderDateContext.ts";
import type { IntermediateOrderSkuRow } from "../../redistribution/models/IntermediateOrderSkuRow.ts";
import { resolveIntermediateOrderArrivalDate } from "./intermediateOrderRedistribution.ts";

function earliest(
  current: string | null,
  candidate: string | null
): string | null {
  if (!candidate) return current;
  if (!current) return candidate;
  return candidate < current ? candidate : current;
}

function latest(current: string, candidate: string): string {
  if (!candidate) return current;
  if (!current) return candidate;
  return candidate > current ? candidate : current;
}

/**
 * Collapses SKU-level Intermediate Order rows into one date summary per Bulk
 * Order. A search resolves a single BO, but the IO grid groups by
 * bulkOrderNumber, so a run can in principle span more than one.
 */
export function buildBulkOrderDateContexts(
  rows: IntermediateOrderSkuRow[] | undefined
): BulkOrderDateContext[] {
  if (!rows || rows.length === 0) {
    return [];
  }

  const byBulkOrder = new Map<string, IntermediateOrderSkuRow[]>();
  for (const row of rows) {
    const existing = byBulkOrder.get(row.bulkOrderNumber) ?? [];
    existing.push(row);
    byBulkOrder.set(row.bulkOrderNumber, existing);
  }

  return [...byBulkOrder.entries()]
    .map(([bulkOrderNumber, bulkOrderRows]) => {
      let vendorCutDate: string | null = null;
      let shipDateRangeStart = "";
      let shipDateRangeEnd = "";
      let lastDistributionDate = "";
      let projectedArrivalDate = "";
      const intermediateOrders = new Set<string>();

      for (const row of bulkOrderRows) {
        intermediateOrders.add(row.intermediatePoNumber);
        vendorCutDate = earliest(vendorCutDate, row.vendorCutDate);
        shipDateRangeStart =
          earliest(shipDateRangeStart || null, row.shipDateRangeStart) ?? "";
        shipDateRangeEnd = latest(shipDateRangeEnd, row.shipDateRangeEnd);
        lastDistributionDate = latest(
          lastDistributionDate,
          row.lastDistributionDate
        );
        // Latest arrival across the IOs: the week by which the whole BO has landed.
        projectedArrivalDate = latest(
          projectedArrivalDate,
          resolveIntermediateOrderArrivalDate(row) ?? ""
        );
      }

      const context: BulkOrderDateContext = {
        bulkOrderNumber,
        intermediateOrderCount: intermediateOrders.size,
        vendorCutDate,
        shipDateRangeStart,
        shipDateRangeEnd,
        lastDistributionDate,
        projectedArrivalDate,
      };
      return context;
    })
    .toSorted((a, b) => a.bulkOrderNumber.localeCompare(b.bulkOrderNumber));
}
